import { useEffect } from "react";

import {
  CallFragment,
  CallNotesV2Query,
  CallNotesV3Query,
  CallScorecardQuery,
  useCallClipsLazyQuery,
  useCallScorecardQuery,
} from "../../graphql";
import { ClipsTabProps } from "../Recording/SidebarTabs/ClipsTab";
import { ScoresTabProps } from "../Recording/SidebarTabs/ScoresTab";
import useCallNotesV2 from "./useCallNotesV2";
import useCallNotesV3 from "./useCallNotesV3";
import useCallTrackers from "./useCallTrackers";
import useCallTraining from "./useCallTraining";
import useCallTranscript from "./useCallTranscript";
import { CallTab } from "./utils";

function isScorecardComplete(
  scorecard: CallScorecardQuery | undefined
): boolean {
  const currentUserScorecard = scorecard?.call?.currentUserScorecard;
  if (currentUserScorecard) {
    if (
      currentUserScorecard.overallScoringEnabled &&
      currentUserScorecard.overallScore == null
    ) {
      return false;
    }
    return currentUserScorecard.competencies.every((competency) =>
      competency.scoringEnabled ? competency.score !== null : true
    );
  }
  return true;
}

type CallSidebarParams = {
  call: Pick<
    CallFragment,
    | "id"
    | "greenhouseScorecardLinkWithId"
    | "leverInterviewLinkWithId"
    | "ashbyFeedbackLinkWithId"
    | "interviewerIds"
    | "scoringEnabled"
    | "iaVersion"
  >;
  currentTab: CallTab | undefined;
  onCallNotesV2QueryCompleted?: (data: CallNotesV2Query) => void;
  onCallNotesV3QueryCompleted?: (data: CallNotesV3Query) => void;
  disableAutofill?: boolean;
  notesForCurrentUserOnly: boolean;
};

/**
 * The types listed here are a bit esoteric in an effort to be DRY. See
 * the definitions of the various helper hooks for more useful types
 */
type CallSidebarReturn = {
  trackers: ReturnType<typeof useCallTrackers>;
  clips: Pick<ClipsTabProps, "clips" | "loading" | "error">;
  training: ReturnType<typeof useCallTraining>;
  notesV2: Omit<ReturnType<typeof useCallNotesV2>, "getCallNotesV2">;
  notesV3: Omit<ReturnType<typeof useCallNotesV3>, "getCallNotesV3">;
  transcript: ReturnType<typeof useCallTranscript>;
  scorecard: Pick<
    ScoresTabProps,
    | "currentUserScorecard"
    | "otherUsersScorecards"
    | "showViewCandidate"
    | "loading"
    | "error"
  > & { isCurrentUserScorecardComplete: boolean };
};

export function useCallSidebar({
  call,
  currentTab,
  onCallNotesV2QueryCompleted,
  onCallNotesV3QueryCompleted,
  disableAutofill,
  notesForCurrentUserOnly,
}: CallSidebarParams): CallSidebarReturn {
  const callId = call.id;
  const trackers = useCallTrackers({ callId });
  const training = useCallTraining({ callId });
  const transcript = useCallTranscript({ callId, notesForCurrentUserOnly });
  const { getCallNotesV2, ...notesV2 } = useCallNotesV2({
    call,
    onCallNotesV2QueryCompleted,
    disableAutofill,
    notesForCurrentUserOnly,
  });
  const { getCallNotesV3, ...notesV3 } = useCallNotesV3({
    call,
    onCallNotesV3QueryCompleted,
    disableAutofill,
    notesForCurrentUserOnly,
  });

  const [getCallClips, callClipsQuery] = useCallClipsLazyQuery({
    variables: { callId },
  });

  const {
    data: scorecardResults,
    loading: scorecardLoading,
    error: scorecardError,
  } = useCallScorecardQuery({
    variables: { callId },
    returnPartialData: true,
  });

  useEffect(() => {
    if (currentTab === CallTab.CLIPS) {
      getCallClips();
    } else if (currentTab === CallTab.NOTES) {
      if (call.iaVersion === 3.0) {
        getCallNotesV3();
      } else {
        getCallNotesV2();
      }
    }
  }, [currentTab]);

  return {
    trackers,
    training,
    transcript,
    notesV2,
    notesV3,
    clips: {
      loading: callClipsQuery.loading,
      error: callClipsQuery.error?.message,
      clips:
        callClipsQuery.data?.call?.callClips ??
        callClipsQuery.previousData?.call?.callClips ??
        [],
    },
    scorecard: {
      loading: scorecardLoading,
      error: scorecardError?.message,
      currentUserScorecard:
        scorecardResults?.call?.currentUserScorecard ?? undefined,
      otherUsersScorecards:
        scorecardResults?.call?.otherUsersScorecards ?? undefined,
      showViewCandidate: scorecardResults?.call?.canViewOtherScores,
      isCurrentUserScorecardComplete: isScorecardComplete(scorecardResults),
    },
  };
}
